import { json } from "@codemirror/lang-json";
import { useComposedRefs } from "@radix-ui/react-compose-refs";
import { CrossCircledIcon } from "@radix-ui/react-icons";
import { githubDark, githubLight } from "@uiw/codemirror-theme-github";
import CodeMirror, {
  Decoration,
  EditorState,
  EditorView,
  Extension,
  Range,
  ReactCodeMirrorRef,
  StateEffect,
  StateField,
} from "@uiw/react-codemirror";
import parse from "json-parse-even-better-errors";
import * as sourceMap from "json-source-map";
import { useEffect, useRef, useState } from "react";
import { useSize } from "use-shared-resize-observer/use-size";

import { Banner } from "@/components/Callout";
import {
  annotateNodeJson,
  error_extension,
  removeError,
  removeSuccess,
  success_extension,
} from "@/components/canvas-editor/annotate-json";
import { AuditLog } from "@/components/canvas-editor/shapes/audit";
import { useEvent } from "@/components/hooks/useEvent";
import { FeedVersion } from "@/lib/types";
import { cn } from "@/lib/utils";

const currentSourceMap = {
  current: null as sourceMap.Pointers | null,
};

function getCustomNodes(sourceMap: sourceMap.Pointers) {
  return Object.keys(sourceMap ?? {})
    .filter((key) => key.endsWith("isCustomNode"))
    .map((key) => key.replace("/metadata/isCustomNode", ""))
    .map((key) => sourceMap[key]);
}

const readOnlyCustomNode = EditorState.transactionFilter.of((tr) => {
  const sourceMap = currentSourceMap.current;
  const customNodes = sourceMap ? getCustomNodes(sourceMap) : [];
  const { from, to } = tr.startState.selection?.ranges[0] ?? {};
  const change = tr.changes as { inserted?: unknown[]; sections?: unknown[] };

  if (
    from &&
    to &&
    (change.inserted?.length ||
      (change.sections?.length && change.sections.length > 2))
  ) {
    for (const customNode of customNodes) {
      if (
        customNode &&
        ((from >= customNode.value.pos && from <= customNode.valueEnd.pos) ||
          (to >= customNode.value.pos && to <= customNode.valueEnd.pos))
      ) {
        return tr.startState.update();
      }
    }
  }

  return tr;
});

const readonly_effect = StateEffect.define<Range<Decoration>[]>();
const readonly_decoration = Decoration.mark({
  class:
    "text-slate-dim relative before:content-[''] before:absolute before:inset-[-2px] before:bg-slate-4 before:z-[-10]",
});
const removeReadonly = StateEffect.define<null>();
const readonly_extension = StateField.define({
  create() {
    return Decoration.none;
  },
  update(value, transaction) {
    value = value.map(transaction.changes);

    for (const effect of transaction.effects) {
      if (effect.is(readonly_effect)) {
        value = Decoration.set(effect.value, true);
      } else if (effect.is(removeReadonly)) {
        value = Decoration.none;
      }
    }

    return value;
  },
  provide: (f) => EditorView.decorations.from(f),
});

const highlight_effect = StateEffect.define<Range<Decoration>[]>();
const removeHighlight = StateEffect.define<null>();
const highlight_extension = StateField.define({
  create() {
    return Decoration.none;
  },
  update(value, transaction) {
    value = value.map(transaction.changes);

    for (const effect of transaction.effects) {
      if (effect.is(highlight_effect)) {
        value = Decoration.set(effect.value, true);
      } else if (effect.is(removeHighlight)) {
        value = Decoration.none;
      }
    }

    return value;
  },
  provide: (f) => EditorView.decorations.from(f),
});

const highlight_decoration = Decoration.mark({
  class: "bg-reda-10 text-red-4",
});

export default function JsonEditor({
  feedVersion,
  onChange,
  codeMirrorRef,
  extensions,
  audit,
}: {
  feedVersion: Omit<FeedVersion, "metadata" | "created_at">;
  onChange?: (newFeed: FeedVersion) => void;
  codeMirrorRef?: React.RefObject<ReactCodeMirrorRef>;
  extensions?: Extension[];
  audit?: AuditLog;
}) {
  const [code, setCode] = useState(JSON.stringify(feedVersion, null, 2));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null!);
  const size = useSize(ref);
  const editorRef = useRef<ReactCodeMirrorRef>(null!);
  const mergeRef = useComposedRefs(codeMirrorRef, editorRef);
  const colorScheme = window.matchMedia?.("(prefers-color-scheme: dark)")
    .matches
    ? "dark"
    : "light";

  try {
    currentSourceMap.current = sourceMap.parse(code).pointers;
  } catch {
    // do nothing
  }

  const onAnnotate = useEvent(() => {
    const view = editorRef.current.view;

    if (audit && currentSourceMap.current && view) {
      annotateNodeJson(
        editorRef.current,
        currentSourceMap.current,
        feedVersion,
        audit.audit,
        undefined,
      );

      return () => {
        view.dispatch({
          effects: [removeError.of(null), removeSuccess.of(null)],
        });
      };
    } else if (view) {
      view.dispatch({
        effects: [removeError.of(null), removeSuccess.of(null)],
      });
    }
  });

  const auditString = JSON.stringify(audit);

  useEffect(onAnnotate, [auditString, onAnnotate]);

  return (
    <div ref={ref} className="h-full w-full relative overflow-auto">
      <CodeMirror
        value={code}
        ref={mergeRef}
        extensions={[
          readonly_extension,
          success_extension,
          error_extension,
          readOnlyCustomNode,
          json(),
          EditorView.lineWrapping,
          EditorView.contentAttributes.of({ "aria-label": "Feed JSON" }),
          highlight_extension,
          ...(extensions ?? []),
        ]}
        className={cn(errorMessage && "[&_.cm-scroller]:pb-[160px]")}
        readOnly={!onChange}
        height={size?.height ? `${size.height}px` : "100%"}
        theme={colorScheme === "dark" ? githubDark : githubLight}
        onChange={(rawNewCode) => {
          const newCode = rawNewCode
            .replace(/[❝❞]/g, '"')
            .replace(/[❛❜]/g, '"');
          setCode?.(newCode);

          try {
            parse(newCode);

            const parsed = JSON.parse(newCode) as FeedVersion;

            if (typeof parsed !== "object") {
              const error = new Error("You need to type JSON in the editor.");
              (error as unknown as { position: number }).position = 0;
              throw error;
            }

            if (typeof parsed.manifest !== "object") {
              const error = new Error("You need to add a root manifest");
              (error as unknown as { position: number }).position = 0;
              throw error;
            }

            if (typeof parsed.manifest?.filter !== "object") {
              const error = new Error("You need to add a root manifest.filter");
              (error as unknown as { position: number }).position = 0;
              throw error;
            }

            onChange?.(JSON.parse(newCode) as FeedVersion);
            editorRef.current.view?.dispatch({
              effects: removeHighlight.of(null),
            });
            setErrorMessage(null);
          } catch (error) {
            console.error(error);
            const { position, message } = error as {
              position: number;
              message: string;
            };
            setErrorMessage(message);

            // Only a parse error knows where it happened. Everything else the
            // change handler can throw -- a manifest the canvas cannot draw,
            // most of all -- has no `position`, and highlighting `NaN` throws
            // out of the editor's own change handler, which is how a rejected
            // edit used to take the whole editor down with it.
            if (newCode.length > 0 && typeof position === "number") {
              editorRef.current.view?.dispatch({
                effects: highlight_effect.of([
                  highlight_decoration.range(
                    Math.max(0, position - 1),
                    position + 1,
                  ),
                ]),
              });
            }
          }
        }}
        onCreateEditor={(view) => {
          if (currentSourceMap.current) {
            const customNodes = getCustomNodes(currentSourceMap.current);

            for (const customNode of customNodes) {
              if (!customNode) {
                continue;
              }

              view.dispatch({
                effects: readonly_effect.of([
                  readonly_decoration.range(
                    customNode.value.pos,
                    customNode.valueEnd.pos,
                  ),
                ]),
              });
            }
          }
        }}
      />
      {errorMessage && (
        <div className="absolute bottom-0 left-0 m-4 bg-slate-app">
          <Banner variant="destructive">
            <div className="flex gap-3 items-center">
              <CrossCircledIcon className="h-6 w-6" />
              <div className="flex flex-col gap-0.5">
                <span className="font-semibold">JSON Validation Error</span>
                <span className="text-sm">{errorMessage}</span>
              </div>
            </div>
          </Banner>
        </div>
      )}
    </div>
  );
}
